/**
 * DeleteCourseConfirmDialog
 *
 * Destructive-confirmation modal for deleting a course. Patterned after
 * Stripe / GitHub's "type the resource name to confirm" — the delete
 * action stays disabled until the admin types DELETE exactly. Friction is
 * the point: course deletion is irreversible and takes lessons,
 * enrollments, and progress data with it.
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '../ui';

const CONFIRM_TOKEN = 'DELETE';

export interface DeleteCourseConfirmDialogProps {
  open: boolean;
  courseTitle: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** Disables the action while the parent's deleteCourse promise is in flight. */
  loading?: boolean;
}

export default function DeleteCourseConfirmDialog({
  open,
  courseTitle,
  onConfirm,
  onCancel,
  loading = false,
}: DeleteCourseConfirmDialogProps) {
  const [typed, setTyped] = useState('');

  // Reset whenever the dialog opens — we don't want a previous DELETE to
  // pre-arm a fresh modal.
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const matches = typed === CONFIRM_TOKEN;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>
        Delete this course?
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2">
            This will permanently delete <strong>{courseTitle || 'this course'}</strong>{' '}
            and all of its content. Lessons, enrollments, and progress
            data will be lost. This cannot be undone.
          </Typography>

          <Box
            sx={{
              p: 2,
              bgcolor: '#fff5f5',
              border: '1px solid',
              borderColor: '#fecaca',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              To confirm, type <strong>{CONFIRM_TOKEN}</strong> below.
            </Typography>
            <TextField
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={CONFIRM_TOKEN}
              fullWidth
              autoFocus
              size="small"
              inputProps={{
                'aria-label': `Type ${CONFIRM_TOKEN} to confirm`,
                autoComplete: 'off',
                spellCheck: false,
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          disabled={!matches || loading}
        >
          {loading ? 'Deleting…' : 'Delete course'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
